// import ReactDOM from "react-dom";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import dayjs from "dayjs";
import filter from "lodash/filter";

import useDeepCompareEffect from "react-use/lib/useDeepCompareEffect";

import isBetween from "dayjs/plugin/isBetween";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { Cron } from "croner";
import { Routes, Route, useNavigate } from "react-router-dom";

import useInteraction from "./useInteraction";
import useQueryBoard from "../../lib/hooks/useQueryBoard";
import { useAppConfig } from "../board";

dayjs.extend(isBetween);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

interface SchedulesProviderProps {
  children: React.ReactNode;
}

interface SchedulesContextValue {
  paused: boolean;
  screensaver: boolean;
  touchDisabled: boolean;
  setPaused: React.Dispatch<React.SetStateAction<boolean>>;
  scheduleScreensaver: (schedules: IScreenSchedule[]) => void;
}

const SchedulesContext = React.createContext<SchedulesContextValue>(null!);

const { Provider } = SchedulesContext;

function isNowInSchedule(
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
  now: dayjs.Dayjs,
): boolean {
  if (end.isAfter(start)) {
    return now.isAfter(start) && now.isBefore(end);
  }

  return now.isAfter(start) || now.isBefore(end);
}

function createCron(schedule: {
  weekdays?: number[];
  time?: string;
  date?: string;
}): string {
  const cron = ["*", "*", "*", "*", "*"];

  const { weekdays, date, time } = schedule;

  if (time) {
    const start = dayjs(time, "HH:mm");

    cron[0] = `${start.get("minute")}`;
    cron[1] = `${start.get("hour")}`;
  }

  if (date) {
    const start = dayjs(date, "YYYY-MM-DD");

    cron[2] = `${start.get("date")}`;
    cron[3] = `${start.get("month") + 1}`;
  }

  if (Array.isArray(weekdays) && weekdays.length > 0) {
    cron[4] = weekdays.join(",");
  }

  return cron.join(" ");
}

function scheduleToCron(schedule: ISchedule): [string, string] {
  const { weekdays, startDate, startTime, endDate, endTime } = schedule;

  const start = createCron({
    weekdays,
    date: startDate,
    time: startDate
      ? dayjs(startDate, "YYYY-MM-DD").startOf("day").format("HH:mm")
      : startTime,
  });

  const end = createCron({
    weekdays,
    date: endDate,
    time: startDate
      ? dayjs(endDate, "YYYY-MM-DD").endOf("day").format("HH:mm")
      : endTime,
  });

  return [start, end];
}

// function postMessage(message: MessageRequest) {
//   if (!window.ReactNativeWebView) {
//     return;
//   }

//   window.ReactNativeWebView.postMessage(JSON.stringify(message));
// }

export const useSchedulesContext = () => useContext(SchedulesContext);

const RESET_DURATION_MS = 10 * 60 * 1000;

const INACTIVITY_DURATION_MS =
  process.env.NODE_ENV !== "production" ? 2000 : 5 * 60 * 1000;

export const Screensaver = () => {
  const { screensaver, paused, setPaused, touchDisabled } =
    useContext(SchedulesContext);

  const visible = screensaver && !paused;

  if (!visible) {
    return null;
  }

  // return ReactDOM.createPortal(
  //   <div
  //     className="position-absolute top-0 bottom-0 right-0 left-0 d-flex h-100 w-100"
  //     style={{ background: "#000" }}
  //     onClick={() => setPaused(true)}
  //   />,
  //   document.getElementById("root")!,
  // );

  return (
    <div
      className="position-absolute top-0 bottom-0 right-0 left-0 d-flex h-100 w-100"
      style={{
        background: "#010101",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        pointerEvents: screensaver && !paused ? "auto" : "none",
      }}
      onClick={touchDisabled ? undefined : () => setPaused(true)}
    />
  );
};

export const ScreensaverRouter = () => {
  const { screensaver, setPaused, scheduleScreensaver, touchDisabled } =
    useSchedulesContext();

  const { data } = useQueryBoard();
  const { preview } = useAppConfig();

  const navigation = useNavigate();

  const sleepSchedules = filter(
    data?.digitalReception?.schedules,
    (item) => item.type === "sleep" && !item.schedule.disabled,
  );

  useDeepCompareEffect(() => {
    if (preview) {
      return;
    }

    scheduleScreensaver(sleepSchedules);
  }, [sleepSchedules, scheduleScreensaver]);

  const onInteractionStateChange = useCallback(
    (state: "idle" | "active") => {
      if (!screensaver) {
        return;
      }

      if (touchDisabled) {
        setPaused(false);
        return;
      }

      if (state === "active") {
        setPaused(true);
      }

      if (state === "idle") {
        setPaused(false);
      }
    },
    [setPaused, screensaver, touchDisabled],
  );

  useInteraction((state) => {
    if (touchDisabled) {
      return;
    }

    if (state === "idle") {
      navigation("/", { replace: true });
    }
  }, RESET_DURATION_MS);

  useInteraction(onInteractionStateChange, INACTIVITY_DURATION_MS);

  return (
    <Routes>
      <Route path="/" element={<Screensaver />} />
      <Route path="*" element={<></>} />
    </Routes>
  );
};

export const SchedulesProvider = React.memo((props: SchedulesProviderProps) => {
  const { children } = props;

  const [paused, setPaused] = useState(false);
  const [screensaver, setScreensaver] = useState(false);

  const [touchDisabled, setTouchDisabled] = useState(false);

  const clearSchedule = useCallback(() => {
    Cron.scheduledJobs.forEach((job) => job.stop());
  }, []);

  const scheduleScreensaver = useCallback(
    (schedules: IScreenSchedule[]) => {
      // console.log("[scheduleScreensaver]: ", schedules);

      clearSchedule();

      let nextScreensaver = false;
      let nextTouchDisabled = false;

      const now = dayjs();

      const dateStart = now.startOf("day").toDate();

      schedules.forEach((item) => {
        const { schedule } = item;

        const weekday = now.day();

        if (Array.isArray(schedule.weekdays) && schedule.weekdays.length > 0) {
          if (!schedule.weekdays.includes(weekday)) {
            return;
          }
        }

        const [startCronTab, endCronTab] = scheduleToCron(schedule);

        const startCron = Cron(startCronTab);
        const endCron = Cron(endCronTab);

        const start = startCron.nextRun(dateStart);
        const end = endCron.nextRun(dateStart);

        if (start && end && isNowInSchedule(dayjs(start), dayjs(end), now)) {
          nextScreensaver = true;
          nextTouchDisabled = !!schedule.touchDisabled;
        }

        startCron.schedule(() => {
          setScreensaver(true);
          setTouchDisabled(!!schedule.touchDisabled);
        });

        endCron.schedule(() => {
          setScreensaver(false);
          setTouchDisabled(false);
        });
      });

      setScreensaver(nextScreensaver);
      setTouchDisabled(nextTouchDisabled);
    },
    [clearSchedule],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => clearSchedule, []);

  // useEffect(() => {
  //   postMessage({
  //     method: "brightness:change",
  //     value: screensaver ? 0 : 1,
  //   });
  // }, [screensaver]);

  const value: SchedulesContextValue = useMemo(
    () => ({
      paused,
      setPaused,
      screensaver,
      scheduleScreensaver,
      touchDisabled,
    }),
    [paused, screensaver, scheduleScreensaver, touchDisabled],
  );

  return <Provider value={value}>{children}</Provider>;
});
